import { FC } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import { TableBody as Body } from '@mui/material';

import { Badge, TableNoData } from '../../shared/uiComponents';
import { HeaderProperties, TableDataProperties } from '../../shared/uiComponents/Table/tableProperties';
import { CustomTableRow, RowTableCell } from '../../shared/uiComponents/Table/tableHelpers';
import UserAction from '../../components/UserAction';

import { DispatchProperties } from '../../redux/store';
import { UserProperties } from '../../redux/API/userAPIProperties';
import { getAdmins, getClinicalSupervisors } from '../../redux/State/userSlice';

export interface AdminDataProperties extends TableDataProperties {
	query: UserProperties[] | null;
}

interface RowRendererProperties {
	data: UserProperties[];
}

export const Headers: HeaderProperties[] = [
	{ id: '8', name: 'Full Name', orderBy: 'fullName' },
	{ id: '1', name: 'Email', orderBy: 'email' },
	{ id: '3', name: 'Email Status', orderBy: 'emailConfirmed', width: '100px' },
	{ id: '4', name: 'Status', orderBy: 'isDeactivated', width: '100px' },
	{ id: '5', name: 'Role' },
	{ id: '6', name: 'Actions', width: '150px' },
];

export const initialTableState: UserProperties = {
	id: '',
	firstName: '',
	lastName: '',
	fullName: '',
	patronymic: '',
	numberOfClients: 0,
	phoneNumber: '',
	address: '',
	notes: '',
	email: '',
	numberOfNotes: 0,
	role: {
		id: '',
		name: '',
		section: {
			id: 0,
			name: '',
		},
	},
	createdBy: {
		id: '',
		fullName: '',
		role: '',
		authorizationType: {
			id: 0,
			name: '',
		},
	},
	authorizationType: {
		id: 0,
		name: '',
	},
	emailConfirmed: false,
	isDeactivated: false,
	isCaseCoordinator: false,
	mbhId: '',
	doeId: '',
	caseCoordinators: [],
};

export const TableBody: FC<RowRendererProperties> = ({ data }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const [params] = useSearchParams();

	const { pathname } = useLocation();

	const isAdmins = pathname.includes('admin');
	const isClinicalSupervisors = pathname.includes('cs');

	const fetchUsers = () => {
		const page = params.get('page') || '1';
		const pageSize = params.get('pageSize') || '8';
		const orderBy = params.get('orderBy') || '';
		const direction = params.get('direction') || '';
		const searchString = params.get('search') || '';
		const section = params.get('status');

		if (isAdmins) {
			dispatch(
				getAdmins({
					page,
					pageSize,
					orderBy,
					direction,
					searchString,
					section,
				}),
			);

			return;
		}

		if (isClinicalSupervisors) {
			dispatch(
				getClinicalSupervisors({
					page,
					pageSize,
					orderBy,
					direction,
					searchString,
				}),
			);
		}
	};

	if (!data?.length) {
		return <TableNoData spanScope={Headers.length} />;
	}

	return (
		<Body>
			{data.map((row, index) => (
				<CustomTableRow key={index}>
					<RowTableCell>{row.fullName}</RowTableCell>
					<RowTableCell>{row.email}</RowTableCell>
					<RowTableCell>
						<Badge
							label={row.emailConfirmed ? 'Confirmed' : 'Pending'}
							sizeType="sm"
							type={row.emailConfirmed ? 'solidSuccess' : 'solidPrimary'}
						/>
					</RowTableCell>
					<RowTableCell>
						<Badge
							label={row.isDeactivated ? 'Inactive' : 'Active'}
							sizeType="sm"
							type={row.isDeactivated ? 'gray' : 'success'}
						/>
					</RowTableCell>
					<RowTableCell>{row.role?.name || '-'}</RowTableCell>
					<RowTableCell>
						<UserAction item={row} fetchUsers={fetchUsers} />
					</RowTableCell>
				</CustomTableRow>
			))}
		</Body>
	);
};
